import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from './NmButton';
import { NmInput } from './NmInput';
import { coachAthlete, type CoachLookup } from '../services/api';

interface AddCoachModalProps {
  onClose: () => void;
  onSent?: () => void;
  isCoached?: boolean;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
  if (typeof msg === 'string' && msg.trim()) return msg;
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

function isCoachChangeError(error: unknown): boolean {
  const msg = apiErrorMessage(error, '').toLowerCase();
  return (
    msg.includes('coach_change') ||
    msg.includes('مربی دارید') ||
    msg.includes('change coach') ||
    msg.includes('تغییر مربی')
  );
}

/** Modal for athletes to find a coach by mobile and send a coaching request. */
export function AddCoachModal({ onClose, onSent, isCoached }: AddCoachModalProps) {
  const { t } = useTranslation();
  const [mobile, setMobile] = useState('');
  const [coach, setCoach] = useState<CoachLookup | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmChange, setConfirmChange] = useState(false);

  async function handleFind() {
    setError('');
    setSuccess('');
    setCoach(null);
    setConfirmChange(false);
    const trimmed = mobile.trim();
    if (trimmed.length < 11) {
      setError(t('app.addCoach.error.invalidMobile'));
      return;
    }
    setLoading(true);
    try {
      const res = await coachAthlete.findCoachByMobile(trimmed);
      setCoach(res.data.data.coach);
    } catch (e: unknown) {
      setError(apiErrorMessage(e, t('app.addCoach.error.notFound')));
    } finally {
      setLoading(false);
    }
  }

  async function sendRequest(replaceCoach: boolean) {
    if (!coach) return;
    setError('');
    setSending(true);
    try {
      await coachAthlete.sendAthleteRequest(coach.id, replaceCoach);
      setSuccess(t('app.addCoach.success'));
      onSent?.();
      window.setTimeout(onClose, 1200);
    } catch (e: unknown) {
      if (isCoachChangeError(e)) {
        setConfirmChange(true);
        setError('');
      } else {
        setError(apiErrorMessage(e, t('app.addCoach.error.sendFailed')));
      }
    } finally {
      setSending(false);
    }
  }

  function handleSend() {
    if (!coach) return;
    if (isCoached || confirmChange) {
      setConfirmChange(true);
      return;
    }
    void sendRequest(false);
  }

  return (
    <div style={s.backdrop} className="title-bar-no-drag" onClick={onClose}>
      <div className="modal-surface" style={s.panel} onClick={(e) => e.stopPropagation()}>
        <header style={s.header}>
          <h2 style={s.title}>{t('app.addCoach.title')}</h2>
          <button type="button" onClick={onClose} style={s.closeBtn} aria-label={t('common.close')}>
            {t('common.close')}
          </button>
        </header>

        {!confirmChange ? (
          <>
            <p style={s.hint}>{t('app.addCoach.hint')}</p>
            <div style={s.row}>
              <NmInput
                label={t('app.addCoach.mobileLabel')}
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder={t('app.addCoach.mobilePlaceholder')}
                inputMode="numeric"
              />
              <NmButton variant="secondary" size="sm" loading={loading} onClick={() => void handleFind()}>
                {t('app.addCoach.find')}
              </NmButton>
            </div>

            {coach && (
              <div style={s.preview}>
                <div style={s.avatar}>{coach.full_name.charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={s.previewName}>{coach.full_name}</div>
                  <div style={s.previewMobile}>{coach.mobile}</div>
                </div>
              </div>
            )}

            {error && <p style={s.error}>{error}</p>}
            {success && <p style={s.success}>{success}</p>}

            <footer style={s.footer}>
              <NmButton variant="ghost" onClick={onClose}>
                {t('common.cancel')}
              </NmButton>
              <NmButton
                variant="primary"
                disabled={!coach}
                loading={sending}
                onClick={() => handleSend()}
              >
                {t('app.addCoach.sendRequest')}
              </NmButton>
            </footer>
          </>
        ) : (
          <>
            <p style={s.confirmText}>{t('coachChange.confirmBody', { name: coach?.full_name ?? '' })}</p>
            <p style={s.confirmHint}>{t('coachChange.confirmHint')}</p>
            <footer style={s.footer}>
              <NmButton variant="ghost" onClick={() => setConfirmChange(false)} disabled={sending}>
                {t('coachChange.cancel')}
              </NmButton>
              <NmButton variant="primary" loading={sending} onClick={() => void sendRequest(true)}>
                {t('coachChange.confirm')}
              </NmButton>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 4000,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  panel: {
    width: '100%',
    maxWidth: 420,
    background: 'var(--surface-0)',
    borderRadius: 'var(--r-4)',
    boxShadow: 'var(--nm-raised)',
    padding: '20px 22px 18px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-display)',
  },
  closeBtn: {
    border: 'none',
    background: 'none',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    color: 'var(--fg-4)',
  },
  hint: {
    margin: '0 0 14px',
    fontSize: 12,
    color: 'var(--fg-3)',
    lineHeight: 1.5,
  },
  row: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-end',
    marginBottom: 12,
  },
  preview: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    padding: '12px 14px',
    borderRadius: 'var(--r-3)',
    background: 'var(--surface-sunken)',
    marginBottom: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #4A90D9, #2563eb)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
  },
  previewName: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--fg-1)',
  },
  previewMobile: {
    fontSize: 11,
    color: 'var(--fg-4)',
    fontFamily: 'var(--font-mono)',
    marginTop: 2,
  },
  confirmText: {
    margin: '0 0 8px',
    fontSize: 14,
    color: 'var(--fg-1)',
    lineHeight: 1.5,
  },
  confirmHint: {
    margin: '0 0 16px',
    fontSize: 12,
    color: 'var(--fg-3)',
    lineHeight: 1.45,
  },
  error: {
    margin: '0 0 10px',
    fontSize: 12,
    color: '#dc2626',
  },
  success: {
    margin: '0 0 10px',
    fontSize: 12,
    color: 'var(--vo2-green-600)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 8,
  },
};
